import React, { useEffect, useRef, useState } from 'react'
import { Button, ButtonBase, Box } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PopoverCard from './PopoverCard';
import BackgroundContainer from '../components/BackgroundContainer';
import BaseHeader from '../components/BaseHeader'
import { animateCardFlip } from '../utils/cardFlipAnimation';

const cardImages = {
  truth: '/gamepictures/truthcard.webp',
  lightDare: '/gamepictures/lightdarecard.webp',
  dare: '/gamepictures/darecard.webp',
  kinkyDare: '/gamepictures/kinkydarecard.webp',
};

const DareButton = styled(ButtonBase)({
  margin: '6px',
  padding: 0,
  border: 'none',
  background: 'transparent',
  borderRadius: '12px',
  aspectRatio: '2.5 / 3.5',
  width: '40%',
  maxWidth: '17vh',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'stretch',
  boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.55)',
});


const DrinkButton = styled(Button)({
  position: 'relative',
  overflow: 'hidden',
  border: '1px solid rgba(160, 55, 110, 0.28)',
  background: `
    radial-gradient(ellipse at 50% 0%, rgba(160, 55, 120, 0.18), transparent 65%),
    linear-gradient(to bottom, #2e0f20 0%, #1e0916 55%, #160710 100%)
  `,
  color: '#d48aae',
  fontSize: '1.05rem',
  fontFamily: 'Playfair Display, serif',
  fontWeight: 500,
  letterSpacing: '0.09em',
  textTransform: 'none',
  borderRadius: '50px',
  padding: '13px 36px',
  marginTop: '10px',
  width: '75%',
  maxWidth: '300px',
  boxShadow: '0 0 18px rgba(140, 40, 95, 0.18), 0 4px 14px rgba(0, 0, 0, 0.55)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'scale(1.03)',
    boxShadow: '0 0 24px rgba(155, 50, 110, 0.28), 0 4px 16px rgba(0, 0, 0, 0.6)',
  },
});


const TruthOrDareCard = ({ fetchDare, fetchLightDare, fetchKinky, fetchTruth, playerOne, playerTwo,
  currentPlayer, currentPlayerTruthsAnswered, currentPlayerLightDaresAnswered, currentPlayerDaresAnswered,
  currentPlayerKinkyDaresAnswered, currentPlayerShotsTaken, gameType, bothDrink, lastTrue, lastLight, lastDare, lastKinky }) => {
  const navigate = useNavigate()
  const gameMode = useSelector((state) => state.contestants.gameMode)
  const [bothDrinkPopover, setBothDrinkPopover] = useState(false)
  const [loading, setLoading] = useState(false);
  const [lockedMessage, setLockedMessage] = useState("");
  const [animate, setAnimate] = useState(false);
  const timeoutRef = useRef(null);
  const fadeAnimationId = 'fade-in-out-message';

  useEffect(() => {
    // Inject once
    if (!document.getElementById(fadeAnimationId)) {
      const style = document.createElement("style");
      style.id = fadeAnimationId;
      style.innerHTML = `
        @keyframes fadeInOutScoped {
          0% { opacity: 0; transform: translateY(10px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-10px); }
        }
        .fade-in-out-scoped {
          animation: fadeInOutScoped 3s ease-in-out;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const showLockedMessage = (text) => {
    // Clear previous timeout if it exists
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  
    setLockedMessage(text);
    setAnimate(false); // reset animation
  
    // restart animation after small delay
    setTimeout(() => {
      setAnimate(true);
    }, 10);
  
    // hide after 3s
    timeoutRef.current = setTimeout(() => {
      setAnimate(false);
      setLockedMessage("");
      timeoutRef.current = null;
    }, 3000);
  };
  
  // optional: clear timer on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleClick = async (fetchFn, cardImage, cardType, event) => {
    if (loading) return;
    setLoading(true);
    const rect = event.currentTarget.getBoundingClientRect();
    try {
      await animateCardFlip(cardImage, cardType, rect, fetchFn);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getButtonAvailability = () => {
    const truth = currentPlayerTruthsAnswered;
    const light = currentPlayerLightDaresAnswered;
    const dare = currentPlayerDaresAnswered;
    
    switch (gameType) {
      case "demo":
        return {
          light: truth >= 2 ? null : `You must answer ${2 - truth} more Truths.`,
          dare: light < 2 ? `You must complete ${2 - light} more Light Dares.` : null,
          kinky: "Disabled in this mode",
        };
      case "comfortable":
        return {
          light: truth >= 3 ? null : `You must answer ${3 - truth} more Truths.`,
          dare: light < 3 ? `You must complete ${3 - light} more Light Dares.` : null,
          kinky: "Disabled in this mode",
        };
      case "explore":
        return {
          dare: truth >= 3 ? null : `You must answer ${3 - truth} more Truths.`,
          kinky: "Disabled in this mode",
        };
      case "open":
        return {
          light: truth >= 2 ? null : `You must answer ${2 - truth} more Truths.`,
          dare:
            light < 2 ? `You must complete ${2 - light} more Light Dares.` : null,
          kinky: dare < 3
            ? `You must complete ${Math.max(0, 3 - dare)} More Dares.`
            : null,
        };
      default:
        return {};
    }
  };

  const buttonsDisabled = getButtonAvailability();


  const handleBothDrink = () => {
    bothDrink()
    setBothDrinkPopover(false)
  }
  return (
    <BackgroundContainer sx={{ overflowY: "hidden" }}>
      <Box sx={{ flexGrow: 1, width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <BaseHeader sx={{
          
          fontSize: 'clamp(2rem, 11vw, 5.5rem)',
          
          paddingTop: 0,
          marginBottom: 0,
        }}>{currentPlayer.name}</BaseHeader>
      </Box>
      <Box width={'100vw'} overflow='hidden' display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" sx={{ gap: 1, paddingBottom: '20px' }}>
        <DareButton
          sx={{
            filter: lastTrue ? 'grayscale(50%) brightness(0.65)' : 'none',
            opacity: lastTrue ? 0.6 : 1,
          }}
          onClick={(e) => {
            if (lastTrue) {
              showLockedMessage("You've finished all the cards in this deck");
            } else {
              handleClick(fetchTruth, cardImages.truth, 'truth', e);
            }
          }}
        >
          <img src={cardImages.truth} alt="Truth" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />
        </DareButton>

        {(gameType === "comfortable" || gameType === "open" || gameType === "demo") && (
          <DareButton
            sx={{
              filter: buttonsDisabled.light || lastLight ? 'grayscale(10%) brightness(0.80)' : 'none',
              opacity: buttonsDisabled.light || lastLight ? 0.6 : 1,
            }}
            onClick={(e) => {
              if (lastLight) {
                showLockedMessage("You've finished all the cards in this deck");
              } else if (buttonsDisabled.light) {
                showLockedMessage(buttonsDisabled.light);
              } else {
                handleClick(fetchLightDare, cardImages.lightDare, 'lightDare', e);
              }
            }}
          >
            <img src={cardImages.lightDare} alt="Light Dare" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />
          </DareButton>
        )}

        <DareButton
          sx={{
            filter: buttonsDisabled.dare || lastDare ? 'grayscale(10%) brightness(0.80)' : 'none',
            opacity: buttonsDisabled.dare || lastDare ? 0.6 : 1,
          }}
          onClick={(e) => {
            if (lastDare) {
              showLockedMessage("You've finished all the cards in this deck");
            } else if (buttonsDisabled.dare) {
              showLockedMessage(buttonsDisabled.dare);
            } else {
              handleClick(fetchDare, cardImages.dare, 'dare', e);
            }
          }}
        >
          <img src={cardImages.dare} alt="Dare" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />
        </DareButton>

        {gameType === "open" && (
          <DareButton
            sx={{
              filter: buttonsDisabled.kinky || lastKinky ? 'grayscale(10%) brightness(0.80)' : 'none',
              opacity: buttonsDisabled.kinky || lastKinky ? 0.6 : 1,
            }}
            onClick={(e) => {
              if (lastKinky) {
                showLockedMessage("You've finished all the cards in this deck");
              } else if (buttonsDisabled.kinky) {
                showLockedMessage(buttonsDisabled.kinky);
              } else {
                handleClick(fetchKinky, cardImages.kinkyDare, 'kinkyDare', e);
              }
            }}
          >
            <img src={cardImages.kinkyDare} alt="Kinky Dare" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />
          </DareButton>
        )}
        {lockedMessage && (
          <Box
            className={animate ? 'fade-in-out-scoped' : ''}
            sx={{
              position: 'absolute',
              bottom: '50vh',
              backgroundColor: 'rgba(20, 10, 30, 0.95)',
              color: '#ffbfe5',
              padding: '10px 16px',
              borderRadius: '10px',
              border: '1px solid rgba(255, 191, 229, 0.2)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              textAlign: 'center',
              fontFamily: 'Playfair Display, serif',
              zIndex: 1000,
            }}
          >
            {lockedMessage}
          </Box>
        )}
        <PopoverCard
          mainText={"Are you sure you want both of you to drink instead of playing your turn?"}
          open={bothDrinkPopover}
          onClose={() => setBothDrinkPopover(false)}
          showButtonOne={true}
          textButtonOne={"Lets Do It!"}
          onButtonOne={handleBothDrink}
        />
        {/* <CustomButton style={{ width: '80%' }} onClick={() => bothDrink()} >Both Drink</CustomButton> */}
      </Box>
      <Box sx={{ flexGrow: 1, width: '100vw', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ flexGrow: 1 }} />
        {gameMode === 'drink' && (
          <DrinkButton onClick={() => setBothDrinkPopover(true)}>
            Both Drink
          </DrinkButton>
        )}
        <Box sx={{ flexGrow: 3 }} />
      </Box>
    </BackgroundContainer>
  )
}

export default TruthOrDareCard